// keyboard_tracking: the native shell's measured keyboard edge,
// delivered by the shell's keyboard tracker (treechat-mobile
// modules/keyboard-edge) straight from its native callback into this page,
// for the bottom editor dock (lib/bottomEditorDock) to place the phone pill by
// (docs/bottom-editor-keyboard-tracker-release.md).
//
// Protocol 1. The shell advertises it before the page loads
// (KNOVIGATOR_NATIVE_CAPS.keyboardEdge = { protocol: 1 }), and every page load
// starts in the shell's legacy mode, where its KeyboardAvoidingView resizes
// the WebView for the keyboard.
//
// 1. The page installs its hook (window.__KNOV_KEYBOARD_EDGE__) and posts
//    { kind: 'keyboardEdgeReady', protocol, pageSession }.
// 2. With the keyboard closed, the shell stops resizing the WebView, starts a
//    new epoch, and calls the hook with
//    { kind: 'keyboardEdgeActivate', protocol, epoch, pageSession, snapshot }.
//    Nothing moves: with no keyboard both modes place the pill on its rail.
// 3. Samples follow through the same hook, from the native callback, for that
//    epoch: { kind: 'keyboardEdgeSample', protocol, epoch, pageSession, seq,
//    time, visible, obstruction, phase, viewportWidth, viewportHeight }.
// 4. { kind: 'keyboardEdgeDeactivate', protocol, epoch, pageSession } hands
//    the page back to legacy; the shell sends it only with the keyboard closed.
//
// Epochs only increase: every change of owner, either way, takes a new one.
// So an activation or a hand-back counts only when it is newer than any the
// page has seen, and a sample only for the epoch it activated with.
// 5. A page that stops using the edge posts { kind: 'keyboardEdgeRelease',
//    protocol, pageSession }, and the shell resizes the WebView again from the
//    next moment the keyboard is closed.
//
// `obstruction` is how far the keyboard covers the WebView from its bottom
// edge, for a keyboard docked at the bottom (0 for a floating keyboard, and
// for a hardware keyboard with no bar); `viewportHeight` is the WebView's
// height. Both are screen distances in CSS pixels at page scale 1 (points on
// iOS, dp on Android): the page maps them into its layout through the visual
// viewport. `visible` is whether a software keyboard is shown at all,
// independent of the obstruction.

import { postMessageToNative } from 'utils/nativeBridge'

export const KEYBOARD_EDGE_TRACKER_STORAGE_KEY = 'treechat:bottom-editor-keyboard-tracker'
export const KEYBOARD_EDGE_COVER_STORAGE_KEY = 'treechat:keyboard-edge-cover'
const MAX_PLAUSIBLE_EDGE_COVER = 1200
// A phone's software keyboard in portrait covers about this much of the
// screen (Pixel 10 Pro XL Gboard 37%, iPhone 16 39%): a first keyboard with
// nothing remembered is expected a little high, so it lands under the pill.
export const FIRST_KEYBOARD_COVER_RATIO = 0.4
export const KEYBOARD_EDGE_PROTOCOL = 1
export const KEYBOARD_EDGE_HOOK = '__KNOV_KEYBOARD_EDGE__'

export const KEYBOARD_EDGE_READY_KIND = 'keyboardEdgeReady'
export const KEYBOARD_EDGE_RELEASE_KIND = 'keyboardEdgeRelease'
export const KEYBOARD_EDGE_ACTIVATE_KIND = 'keyboardEdgeActivate'
export const KEYBOARD_EDGE_SAMPLE_KIND = 'keyboardEdgeSample'
export const KEYBOARD_EDGE_DEACTIVATE_KIND = 'keyboardEdgeDeactivate'

export type KeyboardEdgePhase = 'closed' | 'opening' | 'open' | 'closing'

// The keyboard as the shell measured it (screen CSS pixels, see above).
export type KeyboardEdge = {
    visible: boolean
    obstruction: number
    phase: KeyboardEdgePhase
    viewportHeight: number | null
}

type EdgeWindow = Window & {
    __KNOV_KEYBOARD_EDGE_TRACKER__?: boolean
    KNOVIGATOR_NATIVE_CAPS?: { keyboardEdge?: { protocol?: unknown } | null } | null
}

function readOverride(): boolean | null {
    if (typeof window === 'undefined') return null

    const override = (window as EdgeWindow).__KNOV_KEYBOARD_EDGE_TRACKER__
    if (typeof override === 'boolean') return override

    try {
        const stored = window.localStorage?.getItem(KEYBOARD_EDGE_TRACKER_STORAGE_KEY)
        if (stored === '1' || stored === 'true') return true
        if (stored === '0' || stored === 'false') return false
    } catch {
        // Storage can be unavailable (private mode, sandboxed previews).
    }

    return null
}

// On for everyone wherever the shell offers the edge; a device can still be
// switched off (or on) by the override above, for QA.
export function keyboardEdgeTrackerEnabled(): boolean {
    const override = readOverride()
    if (override != null) return override
    return true
}

export function shellOffersKeyboardEdge(caps: EdgeWindow['KNOVIGATOR_NATIVE_CAPS']): boolean {
    return caps?.keyboardEdge?.protocol === KEYBOARD_EDGE_PROTOCOL
}

const PHASES: ReadonlySet<string> = new Set(['closed', 'opening', 'open', 'closing'])

type EdgeFields = KeyboardEdge

export type KeyboardEdgeMessage =
    | { kind: 'activate'; epoch: number; pageSession: string; snapshot: EdgeFields }
    | { kind: 'sample'; epoch: number; pageSession: string; seq: number; edge: EdgeFields }
    | { kind: 'deactivate'; epoch: number; pageSession: string }

function readEdgeFields(value: unknown): EdgeFields | null {
    if (!value || typeof value !== 'object') return null
    const { visible, obstruction, phase, viewportHeight } = value as Record<string, unknown>
    if (typeof visible !== 'boolean') return null
    if (typeof obstruction !== 'number' || !Number.isFinite(obstruction)) return null
    if (typeof phase !== 'string' || !PHASES.has(phase)) return null
    return {
        visible,
        obstruction: Math.max(0, obstruction),
        phase: phase as KeyboardEdgePhase,
        viewportHeight:
            typeof viewportHeight === 'number' &&
            Number.isFinite(viewportHeight) &&
            viewportHeight > 0
                ? viewportHeight
                : null,
    }
}

// A message from the shell's hook call, or null for anything this page does
// not speak.
export function parseKeyboardEdgeMessage(raw: unknown): KeyboardEdgeMessage | null {
    if (!raw || typeof raw !== 'object') return null
    const message = raw as Record<string, unknown>
    if (message.protocol !== KEYBOARD_EDGE_PROTOCOL) return null
    const { epoch, pageSession } = message
    if (typeof epoch !== 'number' || !Number.isInteger(epoch)) return null
    if (typeof pageSession !== 'string' || !pageSession) return null

    if (message.kind === KEYBOARD_EDGE_ACTIVATE_KIND) {
        const snapshot = readEdgeFields(message.snapshot)
        return snapshot ? { kind: 'activate', epoch, pageSession, snapshot } : null
    }
    if (message.kind === KEYBOARD_EDGE_SAMPLE_KIND) {
        const { seq } = message
        if (typeof seq !== 'number' || !Number.isInteger(seq)) return null
        const edge = readEdgeFields(message)
        return edge ? { kind: 'sample', epoch, pageSession, seq, edge } : null
    }
    if (message.kind === KEYBOARD_EDGE_DEACTIVATE_KIND) {
        return { kind: 'deactivate', epoch, pageSession }
    }
    return null
}

export type KeyboardEdgeAdapterEnvironment = {
    post(message: string): void
    install(hook: (message: unknown) => void): () => void
    newSession(): string
    // While the edge streams, the page's viewport must not react to the
    // keyboard itself (see resolveInteractiveWidgetContent).
    overlayKeyboard(on: boolean): void
    // A keyboard landed open covering this much: the next one is expected
    // there (see rememberedKeyboardEdgeCover).
    rememberCover?(cover: number): void
}

// The page declares interactive-widget=resizes-content, and Android's WebView
// honours it from the IME insets it is dispatched, whatever the shell does
// natively: it shrinks the layout viewport for the keyboard on its own. Under
// the shell's measured edge the page lifts its composer itself, so the
// viewport has to leave the keyboard alone — overlays-content — or the pill
// is lifted a second time, over the navbar, with the whole shrunk page. Only
// ever switched with the keyboard closed, where both place everything alike.
// WebKit ignores interactive-widget.
export function resolveInteractiveWidgetContent(content: string, overlay: boolean): string {
    const widget = overlay ? 'overlays-content' : 'resizes-content'
    const parts = String(content || '')
        .split(',')
        .map(part => part.trim())
        .filter(part => part && !/^interactive-widget\s*=/i.test(part))
    return [...parts, `interactive-widget=${widget}`].join(', ')
}

export function createKeyboardEdgeAdapter(env: KeyboardEdgeAdapterEnvironment) {
    const listeners = new Set<() => void>()
    let pageSession: string | null = null
    let uninstall: (() => void) | null = null
    // The newest owner change seen for this page session, and the epoch the
    // edge is streamed under while the shell streams it.
    let latestEpoch = -1
    let epoch: number | null = null
    let lastSeq = -1
    let edge: KeyboardEdge | null = null

    const emit = () => listeners.forEach(listener => listener())

    const receive = (raw: unknown) => {
        const message = parseKeyboardEdgeMessage(raw)
        if (!message || !pageSession || message.pageSession !== pageSession) return

        if (message.kind === 'activate' || message.kind === 'deactivate') {
            // An owner change older than one already applied is a straggler.
            if (message.epoch <= latestEpoch) return
            latestEpoch = message.epoch
            lastSeq = -1
            if (message.kind === 'activate') {
                epoch = message.epoch
                edge = { ...message.snapshot }
            } else {
                epoch = null
                edge = null
            }
            env.overlayKeyboard(epoch != null)
            emit()
            return
        }
        if (epoch == null || message.epoch !== epoch) return
        // Samples of this epoch in order; an older one arriving late is
        // superseded by what was already applied.
        if (message.seq <= lastSeq) return
        lastSeq = message.seq
        edge = { ...message.edge }
        if (edge.visible && edge.phase === 'open' && edge.obstruction > 0) {
            env.rememberCover?.(edge.obstruction)
        }
        emit()
    }

    return {
        // Install the hook, then tell the shell this page can take the edge.
        start() {
            if (pageSession) return
            pageSession = env.newSession()
            uninstall = env.install(receive)
            env.post(
                JSON.stringify({
                    kind: KEYBOARD_EDGE_READY_KIND,
                    protocol: KEYBOARD_EDGE_PROTOCOL,
                    pageSession,
                }),
            )
        },
        // Hand the WebView's resizing back to the shell (from its next closed
        // keyboard) and stop listening.
        stop() {
            if (!pageSession) return
            env.post(
                JSON.stringify({
                    kind: KEYBOARD_EDGE_RELEASE_KIND,
                    protocol: KEYBOARD_EDGE_PROTOCOL,
                    pageSession,
                }),
            )
            uninstall?.()
            uninstall = null
            pageSession = null
            const wasActive = epoch != null
            latestEpoch = -1
            epoch = null
            lastSeq = -1
            edge = null
            if (wasActive) {
                env.overlayKeyboard(false)
                emit()
            }
        },
        receive,
        // The shell's edge while it streams it to this page; null in legacy.
        current(): KeyboardEdge | null {
            return epoch != null ? edge : null
        },
        isActive(): boolean {
            return epoch != null
        },
        subscribe(listener: () => void) {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
    }
}

export type KeyboardEdgeAdapter = ReturnType<typeof createKeyboardEdgeAdapter>

function newPageSession(): string {
    try {
        const uuid = window.crypto?.randomUUID?.()
        if (uuid) return uuid
    } catch {
        // Fall through to a random id.
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

// The last keyboard's landed cover, kept across page loads like
// keyboardHeightMemory: a page busy opening the editor paints none of the
// edge's samples while the keyboard rises, so the pill is placed where the
// last keyboard landed at the focus (bottomEditorDock expectKeyboard).
let rememberedCover: number | null = null

function plausibleCover(value: unknown): number {
    const cover = Number(value)
    return Number.isFinite(cover) && cover > 0 && cover <= MAX_PLAUSIBLE_EDGE_COVER
        ? Math.round(cover)
        : 0
}

export function rememberedKeyboardEdgeCover(): number {
    if (rememberedCover == null) {
        try {
            rememberedCover = plausibleCover(
                window.localStorage?.getItem(KEYBOARD_EDGE_COVER_STORAGE_KEY),
            )
        } catch {
            rememberedCover = 0
        }
    }
    return rememberedCover
}

// Where the coming keyboard's edge is expected at a focus: the last landed
// edge; failing that, the last keyboard the shell reported (keyboardHeightMemory),
// which on Android stops at the navigation bar the edge covers too; failing
// that, a typical keyboard for this viewport.
export function resolveExpectedEdgeCover({
    remembered,
    legacyHeight,
    isAndroid,
    systemControlsBottom,
    viewportHeight,
}: {
    remembered: number
    legacyHeight: number
    isAndroid: boolean
    systemControlsBottom: number
    viewportHeight: number
}): number {
    const last = plausibleCover(remembered)
    if (last) return last
    const legacy = plausibleCover(legacyHeight)
    if (legacy) {
        return legacy + (isAndroid ? Math.max(0, Math.round(Number(systemControlsBottom) || 0)) : 0)
    }
    return plausibleCover((Number(viewportHeight) || 0) * FIRST_KEYBOARD_COVER_RATIO)
}

function rememberKeyboardEdgeCover(value: number) {
    const cover = plausibleCover(value)
    if (!cover || cover === rememberedCover) return
    rememberedCover = cover
    try {
        window.localStorage?.setItem(KEYBOARD_EDGE_COVER_STORAGE_KEY, String(cover))
    } catch {
        // Storage can be unavailable (private mode, sandboxed previews).
    }
}

let pageAdapter: KeyboardEdgeAdapter | null = null

// The page's one adapter. PanelController starts it with the dock when the
// shell offers the edge and the feature is on; the dock reads it.
export function getKeyboardEdgeAdapter(): KeyboardEdgeAdapter {
    if (!pageAdapter) {
        pageAdapter = createKeyboardEdgeAdapter({
            post: message => {
                postMessageToNative(message)
            },
            install: hook => {
                const target = window as unknown as Record<string, unknown>
                target[KEYBOARD_EDGE_HOOK] = hook
                return () => {
                    if (target[KEYBOARD_EDGE_HOOK] === hook) delete target[KEYBOARD_EDGE_HOOK]
                }
            },
            newSession: newPageSession,
            overlayKeyboard: on => {
                const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
                if (!meta) return
                const next = resolveInteractiveWidgetContent(meta.content, on)
                if (meta.content !== next) meta.content = next
            },
            rememberCover: rememberKeyboardEdgeCover,
        })
    }
    return pageAdapter
}

export function peekKeyboardEdge(): KeyboardEdge | null {
    return pageAdapter ? pageAdapter.current() : null
}

export function keyboardEdgeActive(): boolean {
    return Boolean(pageAdapter?.isActive())
}
